import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

/* ─── Max-width map ─── */
const MAX_WIDTH_MAP = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  full: "max-w-none",
} as const;

/* ─── Types ─── */
interface PageTab {
  id: string;
  label: string;
}

interface PageShellProps {
  /** Page title shown in the sticky header */
  title?: string;
  /** Route to navigate back to. Shows back button if provided. */
  backTo?: string;
  /** Additional actions shown in the header (right side) */
  actions?: React.ReactNode;
  /** Custom header content. Overrides title/back/actions when provided. */
  header?: React.ReactNode;
  /** Tab configuration */
  tabs?: PageTab[];
  activeTab?: string;
  onTabChange?: (id: string) => void;
  /** Content area max width. Default: '3xl' */
  maxWidth?: keyof typeof MAX_WIDTH_MAP;
  /** Additional className for content area */
  contentClassName?: string;
  children: React.ReactNode;
}

/**
 * Two-layer architecture — Layer 2: Page Shell
 *
 * Wraps every page with:
 *   - Sticky page header (title, back nav, actions, tabs)
 *   - Centered content area with consistent max-width & padding
 *
 * Layer 1 (Global Nav) lives in __root.tsx.
 */
export function PageShell({
  title,
  backTo,
  actions,
  header,
  tabs,
  activeTab,
  onTabChange,
  maxWidth = "3xl",
  contentClassName,
  children,
}: PageShellProps) {
  const maxWidthClass = MAX_WIDTH_MAP[maxWidth];

  return (
    <div className="bg-background">
      {/* ── Sticky Page Header ── */}
      {/* top-14 = below Layer 1 global nav (h-14). z-40 below nav's z-50. */}
      <div className="sticky top-0 sm:top-14 z-40 bg-card/80 backdrop-blur-md border-b">
        {header ? (
          /* Custom header (e.g. Feed search bar) */
          header
        ) : (
          /* Standard header: back + title + actions */
          <div className="flex items-center gap-3 px-6 py-3">
            {backTo && (
              <Link to={backTo}>
                <button className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              </Link>
            )}
            {title && <h1 className="text-lg font-semibold">{title}</h1>}
            <div className="flex-1" />
            {actions}
          </div>
        )}

        {/* ── Tabs (rendered below header, still sticky) ── */}
        {tabs && tabs.length > 0 && (
          <div className="flex border-t">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange?.(tab.id)}
                className={cn(
                  "flex-1 py-2.5 text-sm font-medium text-center border-b-2 transition-colors",
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Content Area ── */}
      {/* px-6 matches Layer 1 nav padding for horizontal alignment */}
      <main className={cn("mx-auto px-6 py-6", maxWidthClass, contentClassName)}>{children}</main>
    </div>
  );
}

/**
 * Feed-specific header component.
 * Extracted here so Feed page doesn't inline its own sticky header.
 */
export function FeedHeader({
  value,
  onChange,
  onRefresh,
  isRefreshing,
}: {
  value: string;
  onChange: (v: string) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
}) {
  return (
    <div className="flex items-center gap-3 px-6 py-2.5">
      {/* Search bar */}
      <div className="flex-1 relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search posts..."
          className="w-full pl-9 pr-8 py-2 text-sm bg-muted rounded-full border-0 focus:ring-2 focus:ring-primary/20 focus:outline-none placeholder:text-muted-foreground/60"
        />
        {value && (
          <button
            onClick={() => onChange("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Refresh */}
      <button
        onClick={onRefresh}
        disabled={isRefreshing}
        className="flex items-center justify-center w-9 h-9 shrink-0 rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
      >
        <svg
          className={cn("h-4 w-4", isRefreshing && "animate-spin")}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
          <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
          <path d="M16 16h5v5" />
        </svg>
      </button>
    </div>
  );
}

/**
 * Tab bar sub-component for pages that need tabs below the main header.
 * Used by Feed (outside PageShell header) for its Feed/Explore tabs.
 */
export function TabBar({
  tabs,
  activeTab,
  onChange,
}: {
  tabs: { id: string; label: string; icon?: React.ComponentType<{ className?: string }> }[];
  activeTab: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex items-center justify-center border-b">
      <div className="flex">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => onChange(tab.id)}
              className={cn(
                "flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-colors",
                activeTab === tab.id
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
